import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@contexts/AuthContext';
import api from '@helpers/api.js';
import { MOCK_CHAT, MOCK_CHAT_ID } from '@pages/Chat/mockChat';

const ChatContext = createContext({
    open: false,
    toggle: () => {},
    close: () => {},
    chats: [],
    loading: true,
    totalUnread: 0,
    activeChat: null,
    openChat: () => {},
    clearActiveChat: () => {},
});

function normalizeChatInfo(chatInfo = {}) {
    const participant =
        chatInfo.user
        || chatInfo.counterpart
        || chatInfo.participant
        || chatInfo.member
        || {};
    const lastMessage =
        chatInfo.last_message
        ?? chatInfo.lastMessage
        ?? chatInfo.last_3?.[0]
        ?? null;
    const lastMessageAt =
        chatInfo.last_message_at
        ?? chatInfo.lastMessageAt
        ?? lastMessage?.timestamps?.created
        ?? lastMessage?.created_at
        ?? null;

    return {
        ...chatInfo,
        user_id: chatInfo.user_id ?? participant.id ?? chatInfo.receiver_id,
        username:
            chatInfo.username
            ?? participant.username
            ?? participant.name
            ?? participant.nickname,
        avatar: chatInfo.avatar ?? participant.avatar,
        email: chatInfo.email ?? participant.email,
        last_message: lastMessage,
        last_message_at: lastMessageAt,
    };
}

function getChatRouteId(pathname) {
    const match = pathname.match(/^\/chat\/([^/]+)\/?$/);
    if (!match) return null;

    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
}

// A conversation we already know (a list item's `id` or an explicit chat_id) —
// never the counterpart's user id, which only says who to talk to.
function getKnownChatId(chatInfo = {}) {
    return chatInfo.chat_id ?? chatInfo.chatId ?? chatInfo.id ?? null;
}

// `/chat?user_id=&id=` is the permanent deep-link form (chat e-mails, the
// computed support link, the app's own /chat route); `/chat/:id` stays for
// in-app navigation once the conversation exists.
function chatPathFor(chatInfo = {}) {
    const chatId = getKnownChatId(chatInfo);
    if (chatId != null) return `/chat/${encodeURIComponent(chatId)}`;
    if (chatInfo.user_id != null) return `/chat?user_id=${encodeURIComponent(chatInfo.user_id)}`;
    return '/chat';
}

function blankToNull(value) {
    if (value == null) return null;
    const trimmed = String(value).trim();
    return trimmed === '' ? null : trimmed;
}

function chatMatchesId(chatInfo, id) {
    if (!chatInfo || id == null) return false;

    return [
        chatInfo.chat_id,
        chatInfo.chatId,
        chatInfo.id,
        chatInfo.user_id,
        chatInfo.user?.id,
        chatInfo.counterpart?.id,
    ].some((candidate) => candidate != null && String(candidate) === String(id));
}

export function ChatProvider({ children }) {
    const { t } = useTranslation('chat');
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeChat, setActiveChat] = useState(null);
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const open = /^\/chat(?:\/[^/]+)?\/?$/.test(location.pathname);
    const routeChatId = getChatRouteId(location.pathname);
    const queryChatId = open && routeChatId == null ? blankToNull(searchParams.get('id')) : null;
    const queryUserId = open && routeChatId == null ? blankToNull(searchParams.get('user_id')) : null;
    const targetChatId = routeChatId ?? queryChatId;
    const isMockRoute = targetChatId === MOCK_CHAT_ID;

    const visibleChats = useMemo(() => {
        if (!isMockRoute) return chats;
        if (chats.some((chat) => chatMatchesId(chat, MOCK_CHAT_ID))) return chats;
        return [MOCK_CHAT, ...chats];
    }, [chats, isMockRoute]);

    const totalUnread = visibleChats.reduce(
        (acc, chat) => acc + (chat.unread_messages || 0),
        0,
    );

    const loadChats = useCallback(async () => {
        try {
            const { data } = await api.get('chat');
            const list = Array.isArray(data) ? data : data?.data || data?.chats || [];
            setChats(list.map(normalizeChatInfo));
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!user) return undefined;
        loadChats();
        const interval = setInterval(loadChats, 30000);
        return () => clearInterval(interval);
    }, [loadChats, user]);

    useEffect(() => {
        if (!open || (targetChatId == null && queryUserId == null)) {
            setActiveChat(null);
            return undefined;
        }

        if (isMockRoute) {
            setActiveChat(MOCK_CHAT);
            return undefined;
        }

        const matches = (chat) => !!chat && (
            (targetChatId != null && chatMatchesId(chat, targetChatId))
            || (queryUserId != null && chatMatchesId(chat, queryUserId))
        );
        const routeStateChat = location.state?.chatInfo;
        const found = chats.find(matches);
        if (found) {
            // Keep navigation-only metadata while allowing the 30-second chat
            // refresh to deliver the current last message to the open chat.
            setActiveChat(normalizeChatInfo({
                ...(matches(routeStateChat) ? routeStateChat : {}),
                ...found,
            }));
            return undefined;
        }

        if (matches(routeStateChat)) {
            setActiveChat(normalizeChatInfo(routeStateChat));
            return undefined;
        }

        if (loading) return undefined;

        const placeholder = { username: t('common:entities.user'), avatar: '', email: '' };

        if (targetChatId == null) {
            // Fresh load of `/chat?user_id=` with no conversation yet: show the
            // counterpart now; the composer creates the chat on the first message.
            setActiveChat(normalizeChatInfo({ ...placeholder, user_id: queryUserId }));

            let cancelled = false;
            api.get('/profile', { handle: queryUserId }).then((response) => {
                const profile = response?.data;
                if (cancelled || !profile?.id) return;
                setActiveChat((current) => (
                    current && String(current.user_id) === String(queryUserId)
                        ? {
                            ...current,
                            username: profile.name || profile.nickname || current.username,
                            avatar: profile.avatar || current.avatar,
                        }
                        : current
                ));
            });
            return () => { cancelled = true; };
        }

        setActiveChat(normalizeChatInfo({
            ...placeholder,
            chat_id: targetChatId,
            user_id: queryUserId ?? undefined,
        }));
        return undefined;
    }, [chats, isMockRoute, loading, location.state, open, queryUserId, t, targetChatId]);

    const close = useCallback(() => {
        if (location.state?.chatOpenedFromApp) {
            navigate(-1);
            return;
        }

        navigate('/', { replace: true });
    }, [location.state, navigate]);

    const toggle = useCallback(() => {
        if (open) {
            close();
            return;
        }

        navigate('/chat', {
            state: {
                chatOpenedFromApp: true,
                chatReturnTo: `${location.pathname}${location.search}`,
            },
        });
    }, [close, location.pathname, location.search, navigate, open]);

    const openChat = useCallback((chatInfo) => {
        const normalized = normalizeChatInfo(chatInfo);
        const alreadyInChat = /^\/chat(?:\/[^/]+)?\/?$/.test(location.pathname);
        const previousState = alreadyInChat ? location.state || {} : {};
        const state = {
            ...previousState,
            chatOpenedFromApp: previousState.chatOpenedFromApp ?? !alreadyInChat,
            chatReturnTo: previousState.chatReturnTo
                ?? `${location.pathname}${location.search}`,
            chatInfo: normalized,
        };

        setActiveChat(normalized);
        navigate(chatPathFor(normalized), {
            replace: alreadyInChat,
            state,
        });
    }, [location.pathname, location.search, location.state, navigate]);

    const clearActiveChat = useCallback(() => {
        const { chatInfo, ...state } = location.state || {};
        setActiveChat(null);
        navigate('/chat', { replace: true, state });
    }, [location.state, navigate]);

    useEffect(() => {
        function handler(event) {
            openChat(event.detail);
        }

        window.addEventListener('start_chat_with', handler);
        return () => window.removeEventListener('start_chat_with', handler);
    }, [openChat]);

    return (
        <ChatContext.Provider value={{
            open,
            toggle,
            close,
            chats: visibleChats,
            loading: isMockRoute ? false : loading,
            totalUnread,
            activeChat,
            openChat,
            clearActiveChat,
        }}>
            {children}
        </ChatContext.Provider>
    );
}

export const useChat = () => useContext(ChatContext);

export function sendMessageTo(userInfo) {
    const detail = normalizeChatInfo({
        ...userInfo,
        user_id: userInfo.user_id ?? userInfo.id,
        id: undefined,
    });
    window.dispatchEvent(new CustomEvent('start_chat_with', { detail }));
}
